import { useEffect, useState, type FormEvent } from "react";
import { Building2, Home, Save } from "lucide-react";
import {
  api,
  ApiError,
  type MyUnitRow,
  type UpdateMyUnitInput,
} from "../../api";
import { useAuth } from "../../auth";
import { Button, Card, Field, PageHeader } from "../../ui";

export function ProfilePage() {
  const { state, refresh } = useAuth();
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "" });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (state.status === "authenticated") {
      setForm({
        firstName: state.me.user.firstName,
        lastName: state.me.user.lastName,
        phone: "",
      });
    }
  }, [state.status]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      await api.patch("/api/me", form);
      await refresh();
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? JSON.stringify(err.body) : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (state.status !== "authenticated") return null;

  return (
    <div className="max-w-3xl">
      <PageHeader title="Profile" subtitle="Edit your personal information." />

      <Card>
        <h2 className="text-ink-800 m-0 mb-3">Personal information</h2>
        <form onSubmit={save}>
          <div className="field-row">
            <Field label="First name">
              <input
                className="input"
                value={form.firstName}
                onChange={(e) =>
                  setForm({ ...form, firstName: e.target.value })
                }
                required
              />
            </Field>
            <Field label="Last name">
              <input
                className="input"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                required
              />
            </Field>
          </div>
          <Field
            label="Email"
            hint="Email changes are managed by Property Management."
          >
            <input className="input" value={state.me.user.email} disabled />
          </Field>
          <Field label="Phone">
            <input
              className="input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="optional"
            />
          </Field>
          {saved && <div className="alert-success">Profile saved.</div>}
          {error && <div className="alert-error">{error}</div>}
          <Button variant="primary" type="submit" loading={busy}>
            <Save size={16} />
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </form>
      </Card>

      <MyUnitsCard />

      <Card>
        <h2 className="text-ink-800 m-0 mb-3">Roles</h2>
        <ul className="divide-y divide-ink-100">
          {state.me.roles.map((r, i) => (
            <li
              key={i}
              className="py-2.5 flex items-center justify-between text-sm"
            >
              <strong className="text-ink-800">
                {r.code.replaceAll("_", " ")}
              </strong>
              <span className="text-xs text-ink-500">
                at {r.buildingId.slice(0, 8)}…
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// My units card.
//
// Lists every unit the principal currently owns and lets them edit
// the soft fields: unitType, location, hasWasherDryer, and a freeform
// ownerNotes string. Unit number, floor, riser, building, and the
// combined-unit flag are read-only here because they are
// building-of-record. The hint copy points owners at Property
// Management when they need one of those changed.
// ---------------------------------------------------------------------------
const UNIT_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "studio", label: "Studio" },
  { value: "one_bedroom", label: "One bedroom" },
  { value: "two_bedroom", label: "Two bedroom" },
  { value: "three_bedroom", label: "Three bedroom" },
];

const UNIT_LOCATION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "north", label: "North" },
  { value: "south", label: "South" },
  { value: "east", label: "East" },
  { value: "west", label: "West" },
  { value: "northeast_corner", label: "Northeast corner" },
  { value: "northwest_corner", label: "Northwest corner" },
  { value: "southeast_corner", label: "Southeast corner" },
  { value: "southwest_corner", label: "Southwest corner" },
];

function MyUnitsCard() {
  const [units, setUnits] = useState<MyUnitRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const r = await api.get<{ data: MyUnitRow[] }>("/api/my/units");
      setUnits(r.data);
    } catch (err) {
      setError(
        err instanceof ApiError ? JSON.stringify(err.body) : String(err),
      );
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  if (units === null && !error) {
    return (
      <Card>
        <h2 className="text-ink-800 m-0 mb-2">My units</h2>
        <p className="text-ink-500 text-sm m-0">Loading…</p>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <h2 className="text-ink-800 m-0 mb-2">My units</h2>
        <div className="alert-error">{error}</div>
      </Card>
    );
  }
  if (!units || units.length === 0) {
    return (
      <Card>
        <h2 className="text-ink-800 m-0 mb-2">My units</h2>
        <p className="text-ink-500 text-sm m-0">
          You don&apos;t currently own a unit on file. If you should, contact
          Property Management.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="text-ink-800 m-0 mb-2 inline-flex items-center gap-2">
        <Building2 size={18} /> My units
      </h2>
      <p className="text-[12.5px] text-ink-500 mb-3">
        Update the layout details for the units you own. Unit number, floor,
        and riser are managed by Property Management.
      </p>
      <ul className="flex flex-col gap-3 m-0 p-0 list-none">
        {units.map((u) => (
          <li key={u.id}>
            <MyUnitEditor unit={u} onSaved={reload} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function MyUnitEditor({
  unit,
  onSaved,
}: {
  unit: MyUnitRow;
  onSaved: () => void | Promise<void>;
}) {
  const [unitType, setUnitType] = useState<string>(unit.unitType ?? "");
  const [location, setLocation] = useState<string>(unit.location ?? "");
  const [hasWasherDryer, setHasWasherDryer] = useState(unit.hasWasherDryer);
  const [ownerNotes, setOwnerNotes] = useState(unit.ownerNotes);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    unitType !== (unit.unitType ?? "") ||
    location !== (unit.location ?? "") ||
    hasWasherDryer !== unit.hasWasherDryer ||
    ownerNotes !== unit.ownerNotes;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const body: UpdateMyUnitInput = {};
      if (unitType !== (unit.unitType ?? "")) {
        body.unitType = unitType === "" ? null : unitType;
      }
      if (location !== (unit.location ?? "")) {
        body.location = location === "" ? null : location;
      }
      if (hasWasherDryer !== unit.hasWasherDryer) {
        body.hasWasherDryer = hasWasherDryer;
      }
      if (ownerNotes !== unit.ownerNotes) {
        body.ownerNotes = ownerNotes;
      }
      await api.patch(`/api/my/units/${unit.id}`, body);
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError ? JSON.stringify(err.body) : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="rounded-md border border-sand-200 bg-white p-4"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="inline-flex items-center gap-2">
          <Home size={16} className="text-ink-500" />
          <span className="text-[15px] font-semibold text-ink-800">
            Unit {unit.unitNumber}
          </span>
          <span className="text-[12px] text-ink-500">at {unit.buildingName}</span>
        </div>
        <span className="text-[11.5px] text-ink-500">
          Floor {unit.floor ?? "—"} · Riser {unit.riserCode}
          {unit.isCombinedUnit ? " · combined unit" : ""}
        </span>
      </div>

      <div className="field-row">
        <Field label="Unit type">
          <select
            className="input"
            value={unitType}
            onChange={(e) => setUnitType(e.target.value)}
          >
            <option value="">Not set</option>
            {UNIT_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Exposure / location">
          <select
            className="input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          >
            <option value="">Not set</option>
            {UNIT_LOCATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field hint="Affects whether new washer/dryer projects go through riser-capacity gating.">
        <label className="inline-flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
          <input
            type="checkbox"
            checked={hasWasherDryer}
            onChange={(e) => setHasWasherDryer(e.target.checked)}
            className="rounded border-ink-300 text-brand-700 focus:ring-brand-500"
          />
          In-unit washer / dryer installed
        </label>
      </Field>

      <Field
        label="Notes"
        hint="Anything Property Management should know about this unit (renovation history, quirks, occupancy plans). Visible to staff only."
      >
        <textarea
          className="textarea"
          value={ownerNotes}
          onChange={(e) => setOwnerNotes(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Optional"
        />
      </Field>

      {saved && !dirty && (
        <div className="alert-success">Unit details saved.</div>
      )}
      {error && <div className="alert-error">{error}</div>}

      <Button variant="primary" type="submit" loading={busy} disabled={!dirty}>
        <Save size={14} />
        Save unit details
      </Button>
    </form>
  );
}
